import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { linkingRules, LinkingSuggestion } from '@/data/linkingRules';
import { analyzePage } from '@/utils/linkAnalyzer';

interface InternalLinkingState {
  suggestions: LinkingSuggestion[];
  contextualLinks: { text: string; url: string; anchor: string }[];
  relatedPages: { title: string; url: string; relevance: number }[];
  isLoading: boolean;
}

export const useInternalLinking = () => {
  const location = useLocation();
  const [state, setState] = useState<InternalLinkingState>({
    suggestions: [],
    contextualLinks: [],
    relatedPages: [],
    isLoading: false
  });

  // Analyser la page actuelle pour des opportunités de liens
  const analyzeCurrentPage = useMemo(() => {
    return () => {
      setState(prev => ({ ...prev, isLoading: true }));
      
      try {
        const pageAnalysis = analyzePage(location.pathname);
        const suggestions = linkingRules.getSuggestionsForPage(location.pathname);
        const relatedPages = linkingRules.getRelatedPages(location.pathname);
        const contextualLinks = linkingRules.getContextualLinks(location.pathname);

        setState({
          suggestions,
          contextualLinks,
          relatedPages,
          isLoading: false
        });
      } catch (error) {
        console.error('Erreur lors de l\'analyse de la page pour le maillage interne:', error);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    analyzeCurrentPage();
  }, [analyzeCurrentPage]);

  // Obtenir des liens suggérés pour un terme spécifique
  const getSuggestedLinksForTerm = (term: string) => {
    return linkingRules.getLinksForKeyword(term);
  };

  // Obtenir des ancres optimisées pour une URL
  const getOptimizedAnchors = (targetUrl: string) => {
    return linkingRules.getOptimizedAnchors(targetUrl);
  };

  // Analyser un texte pour trouver des opportunités de liens
  const analyzeTextForLinks = (text: string) => {
    return linkingRules.analyzeTextForLinkOpportunities(text);
  };

  // Générer un maillage automatique pour une page
  const generateAutomaticLinking = (content: string) => {
    return linkingRules.generateAutomaticLinks(content, location.pathname);
  };

  return {
    ...state,
    getSuggestedLinksForTerm,
    getOptimizedAnchors,
    analyzeTextForLinks,
    generateAutomaticLinking,
    refresh: analyzeCurrentPage
  };
};

export default useInternalLinking;